import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, beginAuthChange, formatRequestError, SESSION_EXPIRED } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=logged
  const [authError, setAuthError] = useState(null);
  const [checking, setChecking] = useState(false);
  const revision = useRef(0);
  const refresh = useCallback(async () => {
    const request = ++revision.current;
    setChecking(true);
    try {
      const { data } = await api.get("/auth/me");
      if (request === revision.current) { setUser(data); setAuthError(null); }
      return data;
    } catch (error) {
      if (request === revision.current) {
        if (error.response?.status === 401) { setUser(false); setAuthError(null); }
        else setAuthError(formatRequestError(error));
      }
      return null;
    } finally {
      if (request === revision.current) setChecking(false);
    }
  }, []);

  useEffect(() => {
    const expired = () => {
      revision.current += 1;
      setUser(false); setAuthError(null); setChecking(false);
    };
    window.addEventListener(SESSION_EXPIRED, expired);
    refresh();
    return () => { revision.current += 1; window.removeEventListener(SESSION_EXPIRED, expired); };
  }, [refresh]);

  useEffect(() => {
    if (!authError) return undefined;
    const recover = () => { if (!document.hidden) refresh(); };
    window.addEventListener("online", recover);
    document.addEventListener("visibilitychange", recover);
    return () => { window.removeEventListener("online", recover); document.removeEventListener("visibilitychange", recover); };
  }, [authError, refresh]);

  const authenticate = async (path, payload) => {
    const request = ++revision.current;
    beginAuthChange();
    try {
      const { data } = await api.post(path, payload, { timeout: 30000 });
      if (request === revision.current) { setUser(data); setAuthError(null); }
      return data;
    } finally {
      if (request === revision.current) setChecking(false);
    }
  };
  const login = (email, password) => authenticate("/auth/login", { email, password });
  const register = (payload) => authenticate("/auth/register", payload);
  const updateUser = (value) => {
    revision.current += 1; beginAuthChange();
    setUser(value); setAuthError(null); setChecking(false);
  };
  const logout = async () => {
    revision.current += 1; beginAuthChange();
    await api.post("/auth/logout", {}, { timeout: 15000 });
    updateUser(false);
  };
  return (
    <AuthContext.Provider value={{ user, setUser: updateUser, login, register, logout, refresh, authError, checking }}>
      {authError && <div role="alert" className="relative z-[60] flex flex-wrap items-center justify-center gap-3 bg-amber-950 px-4 py-3 text-sm text-amber-100">
        <span>Connexion momentanément indisponible.</span>
        <button type="button" disabled={checking} onClick={refresh} className="font-bold underline disabled:opacity-50">{checking ? "Nouvel essai…" : "Réessayer"}</button>
      </div>}
      {children}
    </AuthContext.Provider>
  );
}
